import apiClient from '@/lib/api-client';

export interface CalendarConnection {
  id: number;
  calendarType: 'google' | 'apple';
  externalCalendarId: string;
  calendarEmail: string;
  calendarName: string;
  syncEnabled: boolean;
  pushToCalendar: boolean;
  pullFromCalendar: boolean;
  active: boolean;
  lastSyncedAt: string | null;
  lastSuccessfulSyncAt: string | null;
  lastSyncError: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface SyncStats {
  created: number;
  updated: number;
  deleted: number;
}

class CalendarService {
  /**
   * Get OAuth authorization URL.
   *
   * `returnTo` names the screen to come back to after connecting — a symbolic
   * key, resolved server-side against a whitelist, never a path (the callback
   * is unauthenticated, so an arbitrary URL would be an open redirect).
   */
  async getAuthUrl(returnTo?: 'onboarding' | 'settings'): Promise<{ authUrl: string }> {
    const response = await apiClient.get<{ data: { auth_url: string } }>(
      '/v1/admin/google_calendar/auth_url',
      { params: returnTo ? { return_to: returnTo } : undefined }
    );
    return { authUrl: response.data.data.auth_url };
  }

  // Handle OAuth callback
  async handleCallback(code: string, state: string): Promise<{ connection: CalendarConnection; syncedEvents: number }> {
    const response = await apiClient.post<{ data: any }>('/v1/admin/google_calendar/callback', { code, state });
    return {
      connection: this.transformConnection(response.data.data.connection),
      syncedEvents: response.data.data.synced_events
    };
  }

  // List calendar connections
  async getConnections(): Promise<CalendarConnection[]> {
    const response = await apiClient.get<{ data: { connections: any[] } }>('/v1/admin/google_calendar/connections');
    return response.data.data.connections.map(this.transformConnection);
  }

  // Disconnect calendar
  async disconnect(connectionId: number): Promise<void> {
    await apiClient.delete(`/v1/admin/google_calendar/connections/${connectionId}`);
  }

  // Trigger manual sync
  async syncNow(connectionId: number): Promise<SyncStats> {
    const response = await apiClient.post<{ data: { synced_events: any } }>(`/v1/admin/google_calendar/connections/${connectionId}/sync`);
    return response.data.data.synced_events;
  }

  // Get ICS feed URL for the current provider
  async getIcsFeedUrl(): Promise<string> {
    const response = await apiClient.get<{ data: { ics_url: string } }>('/v1/admin/ics_feed');
    return response.data.data.ics_url;
  }

  // Regenerate ICS feed token (invalidates old URL)
  async regenerateIcsFeedToken(): Promise<string> {
    const response = await apiClient.post<{ data: { ics_url: string } }>('/v1/admin/ics_feed/regenerate');
    return response.data.data.ics_url;
  }

  private transformConnection(raw: any): CalendarConnection {
    return {
      id: raw.id,
      calendarType: raw.calendar_type,
      externalCalendarId: raw.external_calendar_id,
      calendarEmail: raw.calendar_email,
      calendarName: raw.calendar_name,
      syncEnabled: raw.sync_enabled,
      pushToCalendar: raw.push_to_calendar,
      pullFromCalendar: raw.pull_from_calendar,
      active: raw.active,
      lastSyncedAt: raw.last_synced_at,
      lastSuccessfulSyncAt: raw.last_successful_sync_at,
      lastSyncError: raw.last_sync_error,
      consecutiveFailures: raw.consecutive_failures,
      createdAt: raw.created_at,
      updatedAt: raw.updated_at,
    };
  }
}

export const calendarService = new CalendarService();
